import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ACTION_SPECS,
  ACTION_TIPS,
  FORCED_STRIKE_AT,
  ROLE_TIPS,
  actionName,
  roleName,
  type ActionType,
  type CardView,
  type GameView,
  type PlayerView,
  type RoleId,
  type Theme,
} from '@betrayal/shared';
import { useStore } from '../store.js';
import { eventText, nameOf } from '../format.js';

export function Game() {
  const { lobby, you, theme, send } = useStore();
  const [pickTargetFor, setPickTargetFor] = useState<ActionType | null>(null);
  if (!lobby?.game) return null;
  const g = lobby.game;
  const who = (id: string) => nameOf(lobby, id);
  const me = g.players.find((p) => p.id === you);
  const spectating = !me;
  const myTurn = g.turnPlayer === you && g.phase === 'awaiting_action';
  const opponents = g.players.filter((p) => p.id !== you);

  return (
    <div className="game">
      <header className="game-header">
        <span className="theme-name">{theme.name}</span>
        <span className="muted">deck {g.deckCount}</span>
        {lobby.deadline && <Countdown deadline={lobby.deadline} />}
      </header>

      {spectating && <div className="banner">👁 Spectating — you joined mid-game</div>}

      <div className="seats">
        {opponents.map((p) => (
          <Seat key={p.id} p={p} name={who(p.id)} turn={g.turnPlayer === p.id} waiting={g.waitingOn.includes(p.id)} theme={theme} />
        ))}
      </div>

      <PendingBanner g={g} theme={theme} who={who} />
      <Log g={g} theme={theme} who={who} />

      {me && (
        <div className={`my-area ${myTurn ? 'my-turn' : ''}`}>
          <div className="my-status">
            <span className="my-name">
              {who(you)} {myTurn && <em>— your turn</em>}
            </span>
            <span className="coins">🪙 {me.coins}</span>
          </div>
          <div className="hand">
            {me.cards.map((c, i) => (
              <CardFace key={i} card={c} theme={theme} big />
            ))}
          </div>
        </div>
      )}

      {me && myTurn && !pickTargetFor && (
        <ActionBar me={me} theme={theme} onAct={(action, needsTarget) => (needsTarget ? setPickTargetFor(action) : send({ t: 'DECLARE', action }))} />
      )}
      {me && pickTargetFor && (
        <TargetPicker
          g={g}
          you={you}
          action={pickTargetFor}
          theme={theme}
          who={who}
          onPick={(target) => {
            send({ t: 'DECLARE', action: pickTargetFor, target });
            setPickTargetFor(null);
          }}
          onCancel={() => setPickTargetFor(null)}
        />
      )}
      {me && (g.phase === 'awaiting_reactions' || g.phase === 'awaiting_block_reactions') && g.waitingOn.includes(you) && (
        <ReactionBar g={g} you={you} theme={theme} who={who} />
      )}
      {me && g.phase === 'awaiting_loss' && g.waitingOn.includes(you) && <LossPicker me={me} theme={theme} />}
      {me && g.phase === 'awaiting_exchange' && g.exchangePool && <ExchangePicker g={g} theme={theme} />}
      {g.phase === 'game_over' && <GameOverPanel g={g} who={who} isHost={lobby.members.find((m) => m.id === you)?.isHost ?? false} />}
    </div>
  );
}

function Seat(props: { p: PlayerView; name: string; turn: boolean; waiting: boolean; theme: Theme }) {
  const { p, name, turn, waiting, theme } = props;
  return (
    <div className={`seat ${turn ? 'turn' : ''} ${p.alive ? '' : 'dead'}`}>
      <div className="seat-name">
        {name} {waiting && <span className="waiting-dot" title="waiting on them">⏳</span>}
      </div>
      <div className="seat-cards">
        {p.cards.map((c, i) => (
          <CardFace key={i} card={c} theme={theme} />
        ))}
      </div>
      <div className="coins">🪙 {p.coins}</div>
    </div>
  );
}

function CardFace({ card, theme, big }: { card: CardView; theme: Theme; big?: boolean }) {
  const cls = `card ${big ? 'big' : ''} ${card.revealed ? 'revealed' : ''}`;
  if (!card.role) return <div className={`${cls} back`}>🂠</div>;
  const role = theme.roles[card.role];
  return (
    <div
      className={cls}
      style={{ borderColor: role.color }}
      title={`${role.name} — ${ROLE_TIPS[card.role]}`}
    >
      <span className="card-glyph">{role.glyph}</span>
      <span className="card-name">{role.name}</span>
    </div>
  );
}

function Countdown({ deadline }: { deadline: number }) {
  const [left, setLeft] = useState(() => Math.max(0, deadline - Date.now()));
  useEffect(() => {
    const t = setInterval(() => setLeft(Math.max(0, deadline - Date.now())), 250);
    return () => clearInterval(t);
  }, [deadline]);
  const s = Math.ceil(left / 1000);
  return <span className={`countdown ${s <= 5 ? 'urgent' : ''}`}>⏱ {s}s</span>;
}

function PendingBanner({ g, theme, who }: { g: GameView; theme: Theme; who: (id: string) => string }) {
  if (!g.pending || g.phase === 'awaiting_action' || g.phase === 'game_over') return null;
  const p = g.pending;
  return (
    <div className="banner pending">
      <strong>{who(p.actor)}</strong> → {actionName(theme, p.action)}
      {p.target ? ` on ${who(p.target)}` : ''}
      {p.claimProven ? ' ✓proven' : ''}
      {p.block ? ` · blocked by ${who(p.block.blocker)} claiming ${roleName(theme, p.block.role)}` : ''}
    </div>
  );
}

function Log({ g, theme, who }: { g: GameView; theme: Theme; who: (id: string) => string }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.scrollTo({ top: ref.current.scrollHeight });
  }, [g.log.length]);
  return (
    <div className="log" ref={ref}>
      {g.log.map((e, i) => (
        <div key={i} className={`log-line ${e.type}`}>
          {eventText(e, theme, who)}
        </div>
      ))}
    </div>
  );
}

/** One-glance mechanic summary for a button subtitle; full text is the tooltip. */
const ACTION_BRIEF: Record<ActionType, string> = {
  income: '+1 coin',
  foreign_aid: '+2 coins',
  strike: '−7 · they lose a card',
  tax: '+3 coins',
  assassinate: '−3 · they lose a card',
  steal: 'take 2 coins',
  exchange: 'swap cards',
};

function ActionBar(props: { me: PlayerView; theme: Theme; onAct: (a: ActionType, needsTarget: boolean) => void }) {
  const { me, theme, onAct } = props;
  const forced = me.coins >= FORCED_STRIKE_AT;
  const actions = (Object.keys(ACTION_SPECS) as ActionType[]).filter((a) => {
    const spec = ACTION_SPECS[a];
    if (forced) return a === 'strike';
    return me.coins >= spec.cost;
  });
  return (
    <div className="dock">
      {forced && <div className="hint">Too rich to hide — you must {actionName(theme, 'strike')}.</div>}
      <div className="actions">
        {actions.map((a) => {
          const spec = ACTION_SPECS[a];
          const claimNote = spec.claims ? ` (claims ${roleName(theme, spec.claims)}, can be challenged)` : '';
          return (
            <button
              key={a}
              className={`action-btn ${spec.claims ? 'claim' : ''}`}
              title={ACTION_TIPS[a] + claimNote}
              onClick={() => onAct(a, spec.needsTarget)}
            >
              <span className="action-title">
                {actionName(theme, a)}
                {spec.claims ? ` ${theme.roles[spec.claims].glyph}` : ''}
              </span>
              <small>{ACTION_BRIEF[a]}</small>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function TargetPicker(props: {
  g: GameView;
  you: string;
  action: ActionType;
  theme: Theme;
  who: (id: string) => string;
  onPick: (id: string) => void;
  onCancel: () => void;
}) {
  const { g, you, action, theme, who, onPick, onCancel } = props;
  const targets = g.players.filter(
    (p) => p.id !== you && p.alive && (action !== 'steal' || p.coins > 0),
  );
  return (
    <div className="dock">
      <div className="hint">{actionName(theme, action)} — pick a target</div>
      <div className="actions">
        {targets.map((p) => (
          <button key={p.id} onClick={() => onPick(p.id)}>
            {who(p.id)} <small>🪙 {p.coins}</small>
          </button>
        ))}
        <button className="ghost" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function ReactionBar(props: { g: GameView; you: string; theme: Theme; who: (id: string) => string }) {
  const { g, you, theme, who } = props;
  const { send } = useStore();
  const p = g.pending!;
  const spec = ACTION_SPECS[p.action];
  const blockWindow = g.phase === 'awaiting_block_reactions';

  const canChallenge = blockWindow || (!!spec.claims && !p.claimProven);
  const blockRoles: RoleId[] = !blockWindow && (spec.blockedByAnyone || p.target === you) ? spec.blockableBy : [];
  const title = blockWindow
    ? `${who(p.block!.blocker)} claims ${roleName(theme, p.block!.role)} to block — believe them?`
    : `${who(p.actor)} declares ${actionName(theme, p.action)}${p.target === you ? ' on YOU' : p.target ? ` on ${who(p.target)}` : ''}`;

  return (
    <div className="dock urgent-dock">
      <div className="hint">{title}</div>
      <div className="actions">
        {canChallenge && (
          <button className="danger" onClick={() => send({ t: 'REACT', reaction: 'challenge' })}>
            Challenge{!blockWindow && spec.claims ? ` ${theme.roles[spec.claims].glyph}` : ''}
          </button>
        )}
        {blockRoles.map((r) => (
          <button key={r} className="claim" onClick={() => send({ t: 'REACT', reaction: 'block', blockRole: r })}>
            Block as {roleName(theme, r)} {theme.roles[r].glyph}
          </button>
        ))}
        <button className="ghost" onClick={() => send({ t: 'REACT', reaction: 'pass' })}>
          Let it pass
        </button>
      </div>
    </div>
  );
}

function LossPicker({ me, theme }: { me: PlayerView; theme: Theme }) {
  const { send } = useStore();
  return (
    <div className="dock urgent-dock">
      <div className="hint">You lose influence — choose a card to give up.</div>
      <div className="actions">
        {me.cards.map((c, i) =>
          c.revealed || !c.role ? null : (
            <button key={i} onClick={() => send({ t: 'LOSE_CARD', cardIndex: i })}>
              {theme.roles[c.role].glyph} {roleName(theme, c.role)}
            </button>
          ),
        )}
      </div>
    </div>
  );
}

function ExchangePicker({ g, theme }: { g: GameView; theme: Theme }) {
  const { send } = useStore();
  const [keep, setKeep] = useState<number[]>([]);
  const pool = g.exchangePool!;
  const need = g.exchangeKeep!;
  const toggle = (i: number) =>
    setKeep((k) => (k.includes(i) ? k.filter((x) => x !== i) : k.length < need ? [...k, i] : k));
  const poolKey = useMemo(() => pool.join(','), [pool]);
  useEffect(() => setKeep([]), [poolKey]);

  return (
    <div className="dock urgent-dock">
      <div className="hint">
        Trade with the deck — keep {need} card{need === 1 ? '' : 's'} ({keep.length}/{need})
      </div>
      <div className="actions">
        {pool.map((r, i) => (
          <button key={i} className={keep.includes(i) ? 'selected' : ''} onClick={() => toggle(i)}>
            {theme.roles[r].glyph} {roleName(theme, r)}
          </button>
        ))}
        <button className="primary" disabled={keep.length !== need} onClick={() => send({ t: 'EXCHANGE_KEEP', keep })}>
          Keep these
        </button>
      </div>
    </div>
  );
}

function GameOverPanel({ g, who, isHost }: { g: GameView; who: (id: string) => string; isHost: boolean }) {
  const { send } = useStore();
  return (
    <div className="overlay">
      <div className="panel game-over">
        <h1>👑 {who(g.winner!)} takes the throne</h1>
        {isHost ? (
          <button className="primary big" onClick={() => send({ t: 'REMATCH' })}>
            Rematch
          </button>
        ) : (
          <p className="muted">Waiting for the host to call a rematch…</p>
        )}
      </div>
    </div>
  );
}
